import { forwardRef } from "react";
import QuickDuePicker from "@/components/QuickDuePicker";
import QuickRecurrencePicker from "@/components/QuickRecurrencePicker";
import { formatDueLabel, todayIso } from "@/lib/dates";
import { recurrenceShortLabel } from "@/lib/recurrence";

export interface NewTaskSchedule {
  dueOn: string | null;
  onDueChange: (next: string | null) => void;
  recurrence: string | null;
  onRecurrenceChange: (next: string | null) => void;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
  placeholder?: string;
  schedule?: NewTaskSchedule;
}

const NewTaskCard = forwardRef<HTMLInputElement, Props>(function NewTaskCard(
  { value, onChange, ariaLabel, placeholder, schedule },
  ref,
) {
  return (
    <div className="task-ghost-card" data-testid="new-task-card">
      <svg className="task-ghost-card-plus" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
      </svg>
      <div className="task-ghost-card-main">
        <input
          ref={ref}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // No submit button in the card, so implicit submission breaks once a picker adds another input.
            if (e.key === "Enter" && !e.nativeEvent.isComposing) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
          aria-label={ariaLabel}
          placeholder={placeholder}
          className="task-input-bare"
        />
        {schedule && <ScheduleMeta dueOn={schedule.dueOn} recurrence={schedule.recurrence} />}
      </div>
      {schedule && (
        <div className="task-ghost-card-actions">
          <QuickDuePicker value={schedule.dueOn} onChange={schedule.onDueChange} iconOnly popoverAlign="end" />
          <QuickRecurrencePicker
            value={schedule.recurrence}
            onChange={(next) => {
              schedule.onRecurrenceChange(next);
              if (next && !schedule.dueOn) schedule.onDueChange(todayIso());
            }}
            iconOnly
            popoverAlign="end"
          />
        </div>
      )}
    </div>
  );
});

function ScheduleMeta({ dueOn, recurrence }: { dueOn: string | null; recurrence: string | null }) {
  const due = dueOn ? formatDueLabel(dueOn) : null;
  const recurrenceLabel = recurrenceShortLabel(recurrence);
  if (!due && !recurrenceLabel) return null;

  return (
    <div className="task-meta" data-testid="new-task-meta">
      {due && <span className={due.tone === "overdue" ? "text-red-500" : ""}>{due.text}</span>}
      {due && recurrenceLabel && <span className="task-meta-sep">·</span>}
      {recurrenceLabel && <span>{recurrenceLabel}</span>}
    </div>
  );
}

export default NewTaskCard;
